import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, Zap, Clock, Mail, Plus, Trash2, Loader2,
  AlertTriangle, CheckCircle, XCircle, Info, RefreshCw,
  Play, Settings, BarChart3, Send, History, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useQueryParamHighlight } from '@/hooks/useQueryParamHighlight';
import { getTerm, useTerms } from '@/lib/branding';

const SEV_CONFIG = {
  critical: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  positive: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
};

const INTERVAL_OPTIONS = [
  { value: 1, label: 'Every hour' },
  { value: 4, label: 'Every 4 hours' },
  { value: 8, label: 'Every 8 hours' },
  { value: 12, label: 'Every 12 hours' },
  { value: 24, label: 'Daily' },
  { value: 48, label: 'Every 2 days' },
  { value: 168, label: 'Weekly' },
];

function formatInterval(hours) {
  const opt = INTERVAL_OPTIONS.find(o => o.value === hours);
  if (opt) return opt.label;
  if (hours < 24) return `Every ${hours}h`;
  return `Every ${Math.round(hours / 24)}d`;
}

function timeAgo(isoStr) {
  if (!isoStr) return 'Never';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AnomalyScanPage() {
  useTerms();
  const term = getTerm('sentinels');
  const termOne = getTerm('sentinels', 'singular');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('schedules');
  const [models, setModels] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  // Create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    model_id: '',
    interval_hours: 24,
    enabled: true,
    notify_on_critical: true,
    recipients: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mods, scheds, hist, elog] = await Promise.all([
        api.getSemanticModels().catch(() => []),
        api.listScanSchedules().catch(() => []),
        api.getScanHistory().catch(() => []),
        api.getScanEmailLog().catch(() => []),
      ]);
      setModels(mods || []);
      setSchedules(scheds || []);
      setHistory(hist || []);
      setEmailLog(elog || []);
    } catch (err) {
      toast.error('Failed to load scan data');
    } finally {
      setLoading(false);
    }
  }, []);

  useQueryParamHighlight('scan', schedules, 'schedule');

  useEffect(() => { loadData(); }, [loadData]);

  const handleRunNow = async () => {
    setScanning(true);
    try {
      await api.runScanNow();
      toast.success('Anomaly scan completed');
      loadData();
    } catch (err) {
      toast.error('Scan failed: ' + err.message);
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    const data = {
      ...form,
      recipients: form.recipients.split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.updateScanSchedule(editingId, data);
        toast.success('Schedule updated');
      } else {
        await api.createScanSchedule(data);
        toast.success('Schedule created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteScanSchedule(id);
      toast.success('Schedule deleted');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggle = async (sched) => {
    try {
      await api.updateScanSchedule(sched.schedule_id, { enabled: !sched.enabled });
      setSchedules(prev => prev.map(s => s.schedule_id === sched.schedule_id ? { ...s, enabled: !s.enabled } : s));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ model_id: '', interval_hours: 24, enabled: true, notify_on_critical: true, recipients: '' });
    setDialogOpen(true);
  };

  const openEdit = (sched) => {
    setEditingId(sched.schedule_id);
    setForm({
      model_id: sched.model_id || '',
      interval_hours: sched.interval_hours || 24,
      enabled: sched.enabled !== false,
      notify_on_critical: sched.notify_on_critical !== false,
      recipients: (sched.recipients || []).join(', '),
    });
    setDialogOpen(true);
  };

  const getModelName = (id) => models.find(m => m.model_id === id)?.name || (id ? id : 'All Models');

  // Stats
  const totalScans = history.length;
  const lastScan = history[0]?.scanned_at;
  const totalInsights = history.reduce((s, h) => s + (h.insights_count || 0), 0);
  const totalEmails = emailLog.length;

  return (
    <div className="h-full flex flex-col" data-testid="anomaly-scan-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50 flex items-center gap-4" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-lg w-8 h-8" data-testid="anomaly-scan-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            {term}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Automated anomaly watches — get alerted when your metrics move</p>
        </div>
        <Button onClick={handleRunNow} disabled={scanning} variant="outline" className="rounded-lg gap-1.5" data-testid="run-scan-now">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run Scan Now
        </Button>
        <Button onClick={openCreate} className="rounded-lg gap-1.5" data-testid="create-schedule-btn">
          <Plus className="w-4 h-4" /> New {termOne}
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="px-6 py-3 border-b border-border/30 flex items-center gap-6" style={{ background: 'var(--glass-bg)' }}>
        {[
          { label: 'Total Scans', value: totalScans, color: 'text-blue-400', icon: BarChart3 },
          { label: 'Last Scan', value: lastScan ? timeAgo(lastScan) : 'Never', color: 'text-emerald-400', icon: Clock },
          { label: 'Anomalies Found', value: totalInsights, color: 'text-amber-400', icon: AlertTriangle },
          { label: 'Emails Sent', value: totalEmails, color: 'text-violet-400', icon: Mail },
          { label: `Active ${term}`, value: schedules.filter(s => s.enabled).length, color: 'text-cyan-400', icon: RefreshCw },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex items-center gap-2" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <Icon className={`w-4 h-4 ${s.color}`} />
              <div>
                <p className={`text-base font-bold font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[9px] text-muted-foreground">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="px-6 pt-3 flex gap-1" style={{ background: 'var(--glass-bg)' }}>
        {[
          { key: 'schedules', label: term, icon: Settings },
          { key: 'history', label: 'Scan History', icon: History },
          { key: 'emails', label: 'Email Log', icon: Send },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-t-lg text-xs font-medium transition-colors border-b-2 ${tab === t.key ? 'bg-muted/30 text-foreground border-primary' : 'text-muted-foreground hover:text-foreground border-transparent hover:bg-muted/10'}`}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.key === 'schedules' && <Badge variant="outline" className="text-[8px] ml-1 h-4">{schedules.length}</Badge>}
              {t.key === 'history' && <Badge variant="outline" className="text-[8px] ml-1 h-4">{history.length}</Badge>}
              {t.key === 'emails' && <Badge variant="outline" className="text-[8px] ml-1 h-4">{emailLog.length}</Badge>}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : tab === 'schedules' ? (
            <div className="space-y-3 max-w-4xl" data-testid="schedules-list">
              {schedules.length === 0 ? (
                <div className="text-center py-16">
                  <Zap className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No {getTerm('sentinels', 'plural', 'lower')} configured</p>
                  <p className="text-xs text-muted-foreground/80 mt-1">Create one to automatically detect anomalies in your data</p>
                  <Button onClick={openCreate} className="mt-4 rounded-lg gap-1.5" data-testid="create-first-schedule">
                    <Plus className="w-4 h-4" /> Create {termOne}
                  </Button>
                </div>
              ) : schedules.map(sched => (
                <div
                  key={sched.schedule_id}
                  className={`glass-card rounded-xl p-5 border transition-all ${sched.enabled ? 'border-border/30' : 'border-border/20 opacity-60'}`}
                  data-testid={`schedule-${sched.schedule_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2.5 rounded-xl ${sched.enabled ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/20 border border-border/30'}`}>
                      <Zap className={`w-5 h-5 ${sched.enabled ? 'text-amber-400' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-heading font-semibold text-foreground">{getModelName(sched.model_id)}</p>
                        <Badge className={`text-[8px] ${sched.enabled ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-muted/30 text-muted-foreground border-border/30'}`}>
                          {sched.enabled ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatInterval(sched.interval_hours)}</span>
                        <span className="flex items-center gap-1"><History className="w-3 h-3" />Last: {sched.last_run ? timeAgo(sched.last_run) : 'Never'}</span>
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{(sched.recipients || []).length} recipient{(sched.recipients || []).length !== 1 ? 's' : ''}</span>
                        {sched.notify_on_critical && <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-red-400" />Critical alerts to admins</span>}
                      </div>
                      {(sched.recipients || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {sched.recipients.map((r, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] font-mono">{r}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch checked={sched.enabled} onCheckedChange={() => handleToggle(sched)} data-testid={`toggle-${sched.schedule_id}`} />
                      <Button variant="ghost" size="icon" onClick={() => openEdit(sched)} className="w-7 h-7 rounded" data-testid={`edit-${sched.schedule_id}`}>
                        <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(sched.schedule_id)} className="w-7 h-7 rounded text-destructive hover:bg-destructive/10" data-testid={`delete-${sched.schedule_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : tab === 'history' ? (
            <div className="max-w-4xl" data-testid="scan-history">
              {history.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">No scan history yet</div>
              ) : (
                <div className="space-y-2">
                  {history.map((h, i) => (
                    <div key={i} className="glass-card rounded-xl p-4 border border-border/30 flex items-center gap-4" data-testid={`history-${i}`}>
                      <div className={`p-2 rounded-lg ${h.insights_count > 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
                        {h.insights_count > 0 ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-foreground">{h.model_name || getModelName(h.model_id)}</p>
                          <Badge variant="outline" className="text-[8px]">{h.scan_type || 'manual'}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{h.summary || `${h.insights_count} findings`}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold font-heading text-foreground">{h.insights_count}</p>
                        <p className="text-[9px] text-muted-foreground">findings</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground/80 flex-shrink-0 w-20 text-right">{timeAgo(h.scanned_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-4xl" data-testid="email-log">
              {emailLog.length === 0 ? (
                <div className="text-center py-16">
                  <Mail className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No alert emails sent yet</p>
                  <p className="text-xs text-muted-foreground/80 mt-1">Emails are sent when anomalies are detected and recipients are configured</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {emailLog.map((log, i) => (
                    <div key={i} className="glass-card rounded-xl p-4 border border-border/30 flex items-center gap-4" data-testid={`email-log-${i}`}>
                      <div className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
                        <Send className="w-4 h-4 text-violet-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">{log.model_name || getModelName(log.model_id)}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground">{log.insights_count} anomalies</span>
                          {log.severity_breakdown?.critical > 0 && (
                            <Badge className="text-[7px] bg-red-500/15 text-red-400 border-red-500/20">{log.severity_breakdown.critical} critical</Badge>
                          )}
                          {log.severity_breakdown?.warning > 0 && (
                            <Badge className="text-[7px] bg-amber-500/15 text-amber-400 border-amber-500/20">{log.severity_breakdown.warning} warning</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(log.recipients || []).map((r, ri) => (
                            <Badge key={ri} variant="outline" className="text-[8px] font-mono">{r}</Badge>
                          ))}
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground/80 flex-shrink-0">{timeAgo(log.sent_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              {editingId ? `Edit ${termOne}` : `New ${termOne}`}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Configure automated anomaly detection with email notifications.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">Semantic Model</Label>
              <Select value={form.model_id || '_all'} onValueChange={v => setForm(p => ({ ...p, model_id: v === '_all' ? '' : v }))}>
                <SelectTrigger className="mt-1 rounded-lg bg-muted/30 border-border/30" data-testid="sched-model">
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Published Models</SelectItem>
                  {models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Scan Frequency</Label>
              <Select value={String(form.interval_hours)} onValueChange={v => setForm(p => ({ ...p, interval_hours: Number(v) }))}>
                <SelectTrigger className="mt-1 rounded-lg bg-muted/30 border-border/30" data-testid="sched-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Email Recipients</Label>
              <Input
                value={form.recipients}
                onChange={e => setForm(p => ({ ...p, recipients: e.target.value }))}
                placeholder="analyst@company.com, cfo@company.com"
                className="mt-1 rounded-lg bg-muted/30 border-border/30 text-foreground"
                data-testid="sched-recipients"
              />
              <p className="text-[9px] text-muted-foreground/80 mt-1">Comma-separated email addresses</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Enabled</Label>
                <Switch checked={form.enabled} onCheckedChange={v => setForm(p => ({ ...p, enabled: v }))} data-testid="sched-enabled" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs text-muted-foreground">Alert admins on critical findings</Label>
                  <p className="text-[9px] text-muted-foreground/70">Auto-email all admin users when critical anomalies are found</p>
                </div>
                <Switch checked={form.notify_on_critical} onCheckedChange={v => setForm(p => ({ ...p, notify_on_critical: v }))} data-testid="sched-notify-critical" />
              </div>
            </div>

            <Button onClick={handleSave} className="w-full rounded-lg" data-testid="save-schedule-btn">
              <Zap className="w-4 h-4 mr-1.5" />
              {editingId ? `Update ${termOne}` : `Create ${termOne}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
